import React, { useEffect, useState } from 'react';
import { getConsent, isPixelConfigured, onConsentChange, setConsent } from '../utils/tracking';

const pill =
  'inline-flex h-11 w-full items-center justify-center rounded-full px-6 text-[15px] font-600 transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-950';

const PILLS = {
  agree: `${pill} bg-gold-400 text-brand-950 hover:bg-gold-500`,
  decline: `${pill} bg-brand-800 text-white hover:bg-brand-700`,
  outline: `${pill} border border-white/70 text-white hover:bg-white/10`
};

const Switch = ({ checked, onChange, disabled, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange?.(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400 disabled:cursor-not-allowed disabled:opacity-60 ${
      checked ? 'bg-gold-400' : 'bg-white/25'
    }`}
  >
    <span
      className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
        checked ? 'translate-x-[22px]' : 'translate-x-0.5'
      }`}
    />
  </button>
);

/**
 * Advertising-cookie consent for the Reddit Pixel: a dark panel pinned to the
 * bottom of the screen. Not a cookie wall — the page stays usable — and
 * "Decline all" is as large and as easy as "Agree & proceed" (UK PECR / ICO
 * guidance). "Manage choices" shows a per-category switch. The footer's
 * "Cookie settings" link reopens it. Renders nothing when no Pixel ID is set.
 */
const ConsentBanner = () => {
  const [open, setOpen] = useState(() => isPixelConfigured() && getConsent().state === 'unset');
  const [managing, setManaging] = useState(false);
  const [advertising, setAdvertising] = useState(() => getConsent().state === 'granted');

  useEffect(
    () =>
      onConsentChange((change) => {
        if (change.reopen && isPixelConfigured()) {
          setAdvertising(getConsent().state === 'granted');
          setOpen(true);
        }
      }),
    []
  );

  if (!open) return null;

  const choose = (state) => {
    setOpen(false);
    setManaging(false);
    setConsent(state);
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie preferences"
      aria-describedby="consent-body"
      className="animate-rise fixed inset-x-0 bottom-0 z-[60] max-h-[85vh] overflow-y-auto rounded-t-2xl border border-b-0 border-white/10 bg-[#03140f] shadow-[0_-12px_40px_rgba(0,0,0,0.35)]"
    >
      <div className="mx-auto flex max-w-[90rem] flex-col gap-6 px-5 py-6 sm:px-8 lg:flex-row lg:items-center lg:justify-between lg:gap-12 lg:px-12 lg:py-7">
        <div className="min-w-0 lg:max-w-4xl">
          <p id="consent-body" className="text-[15px] leading-relaxed text-white/90 sm:text-base">
            ParcelBuddy uses essential cookies to keep the site working and, with your permission, an
            advertising cookie (Reddit Pixel) to learn which of our ads help people report delivery
            issues. Your name, contact details and report details are never shared with it. You can
            agree, decline, or manage your choices — reporting works the same either way.
          </p>

          {managing && (
            <ul className="mt-5 divide-y divide-white/10 rounded-xl border border-white/10 bg-white/[0.03]">
              <li className="flex items-center justify-between gap-4 px-4 py-3.5">
                <div className="min-w-0">
                  <p className="text-sm font-600 text-white">Essential</p>
                  <p className="text-[13px] text-white/60">Keeps the site working and remembers this choice. Always on.</p>
                </div>
                <Switch checked disabled label="Essential cookies (always on)" />
              </li>
              <li className="flex items-center justify-between gap-4 px-4 py-3.5">
                <div className="min-w-0">
                  <p className="text-sm font-600 text-white">Advertising</p>
                  <p className="text-[13px] text-white/60">Reddit Pixel — measures whether our Reddit ads led to a report.</p>
                </div>
                <Switch checked={advertising} onChange={setAdvertising} label="Advertising cookies" />
              </li>
            </ul>
          )}
        </div>

        <div className="flex w-full shrink-0 flex-col gap-2.5 sm:w-72">
          <button type="button" className={PILLS.agree} onClick={() => choose('granted')}>
            Agree &amp; proceed
          </button>
          <button type="button" className={PILLS.decline} onClick={() => choose('denied')}>
            Decline all
          </button>
          {managing ? (
            <button
              type="button"
              className={PILLS.outline}
              onClick={() => choose(advertising ? 'granted' : 'denied')}
            >
              Save choices
            </button>
          ) : (
            <button type="button" className={PILLS.outline} onClick={() => setManaging(true)}>
              Manage choices
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsentBanner;
